import {
  BaseMutationOptions,
  BaseQueryOptions,
  BaseUseMutationResult,
  BaseUseQueryResult,
  useBaseMutation,
  useBaseQuery,
} from "@bounce/shared/requests";
import {axiosPartnerPlatformInstance} from "api/ServerApiManager";
import {AxiosResponse} from "axios";
import {DocumentRequestPage} from "@bounce/shared/declarations";
import {GridPaginationModel, GridSortModel} from "@mui/x-data-grid";
import {DEFAULT_PAGE_SIZE} from "@bounce/shared/ui";
import {IDocumentRequestFilter} from "../../../pages/document-requests/utils";

type ActionRequiredVariables = {
  partnerCode?: string;
};

type ResolveDocumentRequestVariables = {
  documentRequestId: number;
};

type DocumentRequestVariables = {
  paginationModel: GridPaginationModel;
  sortModel: GridSortModel;
  filter: IDocumentRequestFilter;
};

const BASE_URL = "/document-requests";
const QUERY_KEY = "document-requests";

const getJsonFilter = (filter: IDocumentRequestFilter) => {
  const filterJson = {
    status: filter.status,
  };
  return {filter_request: JSON.stringify(filterJson)};
};

export const getDocumentRequestsActionRequired = async (variables: ActionRequiredVariables): Promise<boolean> => {
  const result = await axiosPartnerPlatformInstance.get<boolean, AxiosResponse<boolean>>(
    `${BASE_URL}/action-required`
  );
  return result.data;
};

export const useDocumentRequestsActionRequired = (
  variables: ActionRequiredVariables,
  options?: BaseQueryOptions<boolean, ActionRequiredVariables>
): BaseUseQueryResult<boolean> => {
  return useBaseQuery<boolean, ActionRequiredVariables>(
    [QUERY_KEY, "action-required", variables.partnerCode],
    getDocumentRequestsActionRequired,
    variables,
    options
  );
};

export const getDocumentRequests = async (variables: DocumentRequestVariables): Promise<DocumentRequestPage> => {
  const page = variables.paginationModel?.page || 1;
  const size = variables.paginationModel?.pageSize || DEFAULT_PAGE_SIZE;
  const sortModel = variables.sortModel[0]
    ? {field: variables.sortModel[0]?.field, sort: variables.sortModel[0]?.sort}
    : {};
  const queryString = new URLSearchParams(getJsonFilter(variables.filter)).toString();

  const result = await axiosPartnerPlatformInstance.get<DocumentRequestPage, AxiosResponse<DocumentRequestPage>>(
    `${BASE_URL}?${queryString}`,
    {
      params: {
        page: page,
        size: size,
        field: sortModel.field,
        sort: sortModel.sort,
      },
    }
  );
  return result.data;
};

export const useGetDocumentRequests = (
  variables: DocumentRequestVariables,
  options?: BaseQueryOptions<DocumentRequestPage, DocumentRequestVariables>
): BaseUseQueryResult<DocumentRequestPage> => {
  const paramsKey = JSON.stringify(variables);
  return useBaseQuery<DocumentRequestPage, DocumentRequestVariables>(["document-requests", paramsKey],
    getDocumentRequests,
    variables,
    {
      ...options,
      staleTime: 0,
      cacheTime: 0,
    }
  );
};

export const resolveDocumentRequest = async (variables: ResolveDocumentRequestVariables): Promise<void> => {
  await axiosPartnerPlatformInstance.put<void, AxiosResponse<void>>(
    `${BASE_URL}/${variables.documentRequestId}/resolve`
  );
};

export const useResolveDocumentRequest = (
  options?: BaseMutationOptions<void, ResolveDocumentRequestVariables>
): BaseUseMutationResult<void, ResolveDocumentRequestVariables> =>
  useBaseMutation(resolveDocumentRequest, {
    ...options,
    prefixKeysToReset: () => [[QUERY_KEY]],
  });
